import { InferType, object, string, boolean, array } from "yup";
import { useMemo } from "react";
import { useFormikContext } from "formik";
import InputField from "@/components/form/InputField";
import VariableInputField from "./shared/VariableInputField";
import SearchableSelectField from "@/components/form/SearchableSelectField";
import CheckboxField from "@/components/form/CheckboxField";
import RlsUserGroupFields, {
  rlsUserGroupSchemaFields,
} from "./shared/RlsUserGroupFields";
import { useEntities } from "@/hooks/useCommonQueries";
import PayloadFieldMapper from "./shared/PayloadFieldMapper";
import { WorkflowStep } from "@/types/breeze/Workflow";

// match_on / update_fields come in as a CSV string from the form; the worker
// expects a JSON array. Transforming inside Yup keeps form state simple while
// still emitting the right shape on submit.
const csvToStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value as string[];
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

export const upsertDataValidationSchema = object({
  parameters: object({
    entity_name: string().required("Entity name is required"),
    payload: string().required("Payload is required"),
    match_on: array()
      .of(string().required())
      .transform((_, original) => csvToStringArray(original))
      .min(1, "At least one match_on field is required")
      .required("match_on is required"),
    on_match: string().oneOf(["Ignore", "Update"]).required(),
    update_fields: array()
      .of(string().required())
      .transform((_, original) => csvToStringArray(original)),
    auto_set_rls: boolean(),
    rls_user_field: string(),
    auto_set_rls_groups: boolean(),
    rls_group_field: string(),
    ...rlsUserGroupSchemaFields,
    loop_over: string(),
  }),
});

export type UpsertDataFormValues = InferType<typeof upsertDataValidationSchema>;

interface UpsertDataFieldsProps {
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}

export default function UpsertDataFields({
  existingSteps = [],
  triggerEntity,
}: UpsertDataFieldsProps) {
  const { data: entities } = useEntities();
  const { values } = useFormikContext<UpsertDataFormValues>();
  const onMatch = values?.parameters?.on_match;
  const selectedEntityName = values?.parameters?.entity_name;

  const entityOptions = useMemo(
    () => entities?.map((e) => ({ value: e.name, label: e.name })) || [],
    [entities],
  );

  return (
    <div>
      <SearchableSelectField
        name="parameters.entity_name"
        label="Entity"
        required
        options={entityOptions}
        searchPlaceholder="Search entities..."
        placeholder="Select entity..."
      />

      <VariableInputField
        name="parameters.loop_over"
        label="Loop Over Array"
        placeholder="{{ $anythink.steps.api_call.data }}"
        description="Optional: Reference a previous step's array output to loop through. If set, this step runs for each item, and you can map fields below using local item properties (e.g. {{ name }})."
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
      />

      <PayloadFieldMapper
        name="parameters.payload"
        entityName={selectedEntityName || ""}
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
        label="Payload"
        required
      />

      <InputField
        name="parameters.match_on"
        label="Match On"
        required
        description="Comma-separated field names used to identify an existing row (e.g. discogs_id)"
      />

      <SearchableSelectField
        name="parameters.on_match"
        label="On Match"
        required
        options={[
          { value: "Ignore", label: "Ignore — leave existing row untouched" },
          {
            value: "Update",
            label: "Update — overwrite payload fields on existing row",
          },
        ]}
        placeholder="Select behaviour..."
      />

      {onMatch === "Update" && (
        <InputField
          name="parameters.update_fields"
          label="Update Fields"
          description="Optional comma-separated allowlist of fields to write when match is found. Leave empty to overwrite all payload fields."
        />
      )}

      <CheckboxField
        name="parameters.auto_set_rls"
        label="Auto-set RLS"
        description="Apply RLS access from the payload's user field on newly created rows"
      />

      <SearchableSelectField
        name="parameters.rls_user_field"
        label="RLS User Field"
        options={[
          { value: "user", label: "user" },
          { value: "user_id", label: "user_id" },
          { value: "owner", label: "owner" },
          { value: "owner_id", label: "owner_id" },
          { value: "created_by", label: "created_by" },
        ]}
        variableToggle
        placeholder="user (default)"
        description="The field name in the payload that contains the user ID for RLS"
      />

      <CheckboxField
        name="parameters.auto_set_rls_groups"
        label="Auto-set RLS Groups"
        description="Apply RLS access from the payload's group field on newly created rows"
      />

      <SearchableSelectField
        name="parameters.rls_group_field"
        label="RLS Group Field"
        options={[
          { value: "group", label: "group" },
          { value: "group_id", label: "group_id" },
          { value: "team", label: "team" },
          { value: "team_id", label: "team_id" },
        ]}
        variableToggle
        placeholder="group (default)"
        description="The field name in the payload that contains the group ID for RLS"
      />

      <RlsUserGroupFields />
    </div>
  );
}
